import React, { useState } from 'react';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { googleAnalyticsService } from '../../services/googleAnalytics';
import { toast } from 'sonner';
import { 
  BarChart3, 
  CheckCircle, 
  AlertCircle, 
  ExternalLink,
  RefreshCw
} from 'lucide-react';

interface GA4OAuthStepProps {
  data?: {
    isConnected?: boolean;
    completed?: boolean;
  };
  onNext: (data: any) => void;
  onBack: () => void;
  onSkip: () => void;
  isFirst: boolean;
  isLast: boolean;
}

export function GA4OAuthStep({ data, onNext, onBack, onSkip }: GA4OAuthStepProps) {
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'connecting' | 'connected' | 'error'>('idle');

  console.log('GA4OAuthStep mounted', { data, connectionStatus });

  const handleConnectOAuth = async () => {
    setIsConnecting(true);
    setConnectionStatus('connecting');
    
    try {
      // Set onboarding flag so the OAuth callback knows we're in onboarding
      sessionStorage.setItem('onboarding_in_progress', 'true');
      
      const result = await googleAnalyticsService.initiateOAuth();
      if (!result.success) {
        throw new Error(result.error || 'Failed to initiate OAuth');
      }
      // OAuth will redirect, so we don't need to handle success here
    } catch (error) {
      console.error('OAuth initiation error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to connect to Google Analytics');
      setConnectionStatus('error');
      setIsConnecting(false);
      // Remove the flag if OAuth initiation fails
      sessionStorage.removeItem('onboarding_in_progress');
    }
  };

  const handleSkipConnection = () => {
    onNext({ skipped: true });
  };

  return (
    <div className="space-y-8">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full mb-4">
          <BarChart3 className="w-8 h-8 text-green-600 dark:text-green-400" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Connect Google Analytics 4
        </h2>
        <p className="text-gray-600 dark:text-gray-300">
          First, we'll connect to your Google Analytics account
        </p>
      </div>

      <Card className="p-6 border-2 border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20">
        <div className="flex items-center mb-3">
          <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center mr-3">
            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
          </div>
          <h3 className="font-semibold text-gray-900 dark:text-white">OAuth Connection</h3>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
          Securely connect using Google OAuth. After connecting, you'll choose which GA4 property to use.
        </p>
        <div className="space-y-2">
          <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
            <CheckCircle className="w-3 h-3 mr-2 text-green-500 dark:text-green-400" />
            Automatic setup
          </div>
          <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
            <CheckCircle className="w-3 h-3 mr-2 text-green-500 dark:text-green-400" />
            Secure token management
          </div>
          <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
            <CheckCircle className="w-3 h-3 mr-2 text-green-500 dark:text-green-400" />
            Real-time data access
          </div>
        </div>
      </Card>

      {connectionStatus === 'connecting' && (
        <Card className="p-6 text-center">
          <RefreshCw className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin mx-auto mb-4" />
          <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Connecting to Google Analytics</h3>
          <p className="text-gray-600 dark:text-gray-300">Please complete the authentication in the popup window...</p>
        </Card>
      )}

      {connectionStatus === 'error' && (
        <Card className="p-6 text-center border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
          <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400 mx-auto mb-4" />
          <h3 className="font-semibold text-red-900 dark:text-red-200 mb-2">Connection Failed</h3>
          <p className="text-red-600 dark:text-red-300 mb-4">Please try again or contact support if the issue persists.</p>
          <Button onClick={handleConnectOAuth} disabled={isConnecting}>
            Try Again
          </Button>
        </Card>
      )}

      {connectionStatus === 'idle' && (
        <div className="text-center">
          {/* Privacy Statement */}
          <Card className="p-4 mb-6 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
            <div className="text-left">
              <h4 className="font-medium text-blue-900 dark:text-blue-200 mb-2 flex items-center">
                <CheckCircle className="w-4 h-4 mr-2" />
                Privacy & Data Protection
              </h4>
              <p className="text-sm text-blue-800 dark:text-blue-300 leading-relaxed">
                Crutan adheres to the Google API Services User Data Policy, including the Limited Use requirements. 
                We do not use, share, or transfer raw or derived user data obtained through Workspace APIs to train, 
                improve, or build generalized AI/ML models. All data processing is performed securely and solely for 
                the individual user's benefit. Data is anonymized and limited to numerical performance metrics and 
                general industry categorization.
              </p>
            </div>
          </Card>

          {/* Google OAuth Button */}
          <Button 
            onClick={handleConnectOAuth} 
            disabled={isConnecting}
            size="lg"
            className="mb-4 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 shadow-sm dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-gray-200 dark:border-gray-600"
          >
            {isConnecting ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Connecting...
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Connect with Google
              </>
            )}
          </Button>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            You'll be redirected to Google to authorize access to your Analytics data
          </p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-6 border-t border-gray-200 dark:border-gray-700">
        <Button variant="ghost" onClick={handleSkipConnection}>
          Skip for now
        </Button>
        
        <Button 
          onClick={handleConnectOAuth} 
          size="lg" 
          disabled={isConnecting}
          className="bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 shadow-sm dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-gray-200 dark:border-gray-600"
        >
          {isConnecting ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Connecting...
            </>
          ) : (
            <>
              <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Connect to Google Analytics
            </>
          )}
        </Button>
      </div>
    </div>
  );
} 